/**
 * PerformanceService — measures execution time of instrumented operations.
 *
 * Wraps async and sync operations with timing, success/failure tracking,
 * and reports metrics to the logger. No external APM vendor is
 * hardcoded — the metrics are emitted as structured logs.
 */

import { LoggerFactory } from '@/lib/logging';
import type { LogContext } from '@/lib/logging';

export interface PerformanceMetric {
  operation: string;
  durationMs: number;
  success: boolean;
  rowsAffected?: number;
  retryCount?: number;
  error?: string;
  context?: LogContext;
}

class PerformanceServiceImpl {
  private logger = LoggerFactory.create();
  private metrics: PerformanceMetric[] = [];
  private readonly maxBufferSize = 100;

  async measure<T>(
    operation: string,
    fn: () => Promise<T>,
    context?: LogContext,
  ): Promise<T> {
    const start = performance.now();
    let success = false;
    let error: string | undefined;

    try {
      const result = await fn();
      success = true;
      return result;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      const durationMs = Math.round(performance.now() - start);
      const metric: PerformanceMetric = {
        operation,
        durationMs,
        success,
        ...(error ? { error } : {}),
        context,
      };
      this.record(metric);
    }
  }

  measureSync<T>(operation: string, fn: () => T, context?: LogContext): T {
    const start = performance.now();
    let success = false;
    let error: string | undefined;

    try {
      const result = fn();
      success = true;
      return result;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      const durationMs = Math.round(performance.now() - start);
      const metric: PerformanceMetric = {
        operation,
        durationMs,
        success,
        ...(error ? { error } : {}),
        context,
      };
      this.record(metric);
    }
  }

  record(metric: PerformanceMetric): void {
    this.metrics.push(metric);
    if (this.metrics.length > this.maxBufferSize) {
      this.metrics.shift();
    }

    if (metric.success) {
      this.logger.debug(`[perf] ${metric.operation} ${metric.durationMs}ms`, {
        ...metric.context,
        durationMs: metric.durationMs,
      });
    } else {
      this.logger.warn(`[perf] ${metric.operation} FAILED ${metric.durationMs}ms`, {
        ...metric.context,
        durationMs: metric.durationMs,
        error: metric.error,
      });
    }
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  clear(): void {
    this.metrics = [];
  }
}

export const performanceService = new PerformanceServiceImpl();
